import i18n from "@/i18n";
import SocketController from "@/services/Socket/socket.controller";
import {
  SimpleOrder,
  simpleOrderSchema,
  SingleOrderRequestResponse,
  singleOrderRequestResponseSchema,
} from "@/types";
import {
  validateArrayWithSchema,
  validateWithSchema,
} from "@/utils/dataValidators.zod";

// #region fetchAllUsersOrders
export async function fetchAllUsersOrders(): Promise<
  | {
      rStatus: "success";
      rData: SimpleOrder[];
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "userOrders",
      {},
      true
    );

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<SimpleOrder>(
        response.data,
        simpleOrderSchema,
        "Simple Order"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid order data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid order data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        rData: [],
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support"),
      };
    }

    if (response.status === 401 || response.status === 403) {
      return {
        rStatus: "error",
        rData: i18n.t("Unauthorized or forbidden action"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Failed to fetch user orders due to server error"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch user orders"),
    };
  } catch (error) {
    console.error("Error fetching user orders:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch user orders"),
    };
  }
}
// #endregion fetchAllUsersOrders

// #region fetchSingleOrder
export async function fetchSingleOrder(orderID: string): Promise<
  | {
      rStatus: "success";
      rData: SingleOrderRequestResponse;
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "orderByID",
      { orderID },
      true
    );

    if (response.status === 200) {
      const validatedData = validateWithSchema<SingleOrderRequestResponse>(
        response.data,
        singleOrderRequestResponseSchema,
        "Single Order Request Response"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid single order data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid single order data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No order found with the provided ID"),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support"),
      };
    }

    if (response.status === 401 || response.status === 403) {
      return {
        rStatus: "error",
        rData: i18n.t("Unauthorized or forbidden action"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Failed to fetch single order due to server error"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch single order"),
    };
  } catch (error) {
    console.error("Error fetching single order:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch single order"),
    };
  }
}
// #endregion fetchSingleOrder

// #region sendRepeatOrder
export async function sendRepeatOrder(orderID: string): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "orderDuplicate",
      { orderID },
      true
    );

    if (response.status === 200 || response.status === 202) {
      return {
        rStatus: "success",
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No order found with the provided ID"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Failed to repeat order due to server error"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support"),
      };
    }

    if (response.status === 401 || response.status === 403) {
      return {
        rStatus: "error",
        rData: i18n.t("Unauthorized or forbidden action"),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to repeat order"),
    };
  } catch (error) {
    console.error("Error repeating order:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to repeat order"),
    };
  }
}
// #endregion sendRepeatOrder

// #region sendUpdateOrderBranch
export async function sendUpdateOrderBranch({
  orderID,
  branchID,
}: {
  orderID: string;
  branchID: string;
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "updateOrderBranch",
      { orderID: orderID, branchID: branchID },
      true
    );

    if (response.status === 200 || response.status === 202) {
      return {
        rStatus: "success",
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No order found with the provided ID"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Failed to update order branch due to server error"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Bad request. Please contact support"),
      };
    }

    if (response.status === 401 || response.status === 403) {
      return {
        rStatus: "error",
        rData: i18n.t("Unauthorized or forbidden action"),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to update order branch"),
    };
  } catch (error) {
    console.error("Error updating order branch:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to update order branch"),
    };
  }
}
// #endregion sendUpdateOrderBranch
